import React, {createContext, useContext, useState, useCallback} from 'react'
import {requestNotificationPermissionForConnect, ensureCookiesWritable} from '../../Utils/cookieManager'

export type ConsentLevel = 'all' | 'necessary' | null

interface CookieConsentContextType {
  consentGiven: boolean
  consentLevel: ConsentLevel
  acceptAll: () => void
  acceptNecessary: () => void
  resetConsent: () => void
}

const CONSENT_KEY = 'adastra_cookie_consent'
const CONSENT_LEVEL_KEY = 'adastra_cookie_consent_level'

const CookieConsentContext = createContext<CookieConsentContextType>({
  consentGiven: false,
  consentLevel: null,
  acceptAll: () => {},
  acceptNecessary: () => {},
  resetConsent: () => {},
})

export const useCookieConsent = () => useContext(CookieConsentContext)

function getStoredConsent(): {given: boolean; level: ConsentLevel} {
  try {
    const given = localStorage.getItem(CONSENT_KEY) === 'true'
    const level = localStorage.getItem(CONSENT_LEVEL_KEY) as ConsentLevel
    return {given, level: given ? level : null}
  } catch {
    return {given: false, level: null}
  }
}

function persistConsent(level: ConsentLevel) {
  try {
    localStorage.setItem(CONSENT_KEY, 'true')
    localStorage.setItem(CONSENT_LEVEL_KEY, level ?? '')
  } catch {
    // localStorage unavailable
  }
}

function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY)
    localStorage.removeItem(CONSENT_LEVEL_KEY)
  } catch {
    // localStorage unavailable
  }
}

export const CookieConsentProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [consentGiven, setConsentGiven] = useState(() => getStoredConsent().given)
  const [consentLevel, setConsentLevel] = useState<ConsentLevel>(() => getStoredConsent().level)

  const runPostConsentActions = useCallback(() => {
    // Ensure cookies are writable (we set them via useCookieSync; this verifies browser allows it)
    ensureCookiesWritable()
    // Request notification permission so user can receive AWS Connect call popups
    requestNotificationPermissionForConnect()
      .then((permission) => {
        if (permission === 'granted') {
          window.dispatchEvent(new CustomEvent('aac-notification-permission-granted'))
        }
      })
      .catch(() => {})
  }, [])

  const acceptAll = useCallback(() => {
    setConsentGiven(true)
    setConsentLevel('all')
    persistConsent('all')
    runPostConsentActions()
  }, [runPostConsentActions])

  const acceptNecessary = useCallback(() => {
    setConsentGiven(true)
    setConsentLevel('necessary')
    persistConsent('necessary')
    runPostConsentActions()
  }, [runPostConsentActions])

  const resetConsent = useCallback(() => {
    setConsentGiven(false)
    setConsentLevel(null)
    clearConsent()
  }, [])

  return (
    <CookieConsentContext.Provider
      value={{consentGiven, consentLevel, acceptAll, acceptNecessary, resetConsent}}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}
